import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { FormProvider } from 'react-hook-form';
import Tabs from '@common/components/Tabs/Tabs';
import SubmissionFlowHeader from '@common/components/SubmissionFlowHeader/SubmissionFlowHeader';
import { ProductHistory } from './tabs/ProductHistory';
import { ProductDetails } from '@components/ProductDetails/ProductDetails';
import { HeaderBreadcrumb, Link } from '@components/SubmissionFlowHeader/styles';
import { AssociatedContent } from './tabs/AssociatedContent';
import { ProductInfo } from './tabs/ProductInfo';
import * as Styled from './tabs/ProductInfo/styles';
import { AssociatedContentReadonlySections, ProductInfoReadonlySections, ProductTabs, SubstanceErrors } from './types';
import {
  getAssociatedProducts,
  getDocumentAfterSelectionChanged,
  getProductType,
  mapErrors,
  prepareUpdateProductDetailsData,
} from './helpers';
import { AlreadyAssociatedProductsModal } from './tabs/AssociatedContent/AlreadyAssociatedProductsModal';
import { useUpdateProductDetails } from './hooks/useUpdateProductDetails';
import { useDuplicateProductError, IDuplicateProductInfo } from './hooks/useDuplicateProductError';
import { DocumentType, Id } from '@common/types';
import SubmissionFlowFooter from '@common/components/SubmissionFlowFooter/SubmissionFlowFooter';
import { DocumentState, IProductDto, ISubmissionDto, ProductChangeType } from '@common/features/submission/types';
import { SubmissionTaskStatuses } from '@common/services/types';

import { useProductForm, IProductForm } from './hooks/useProductForm';
import { NotifyModal } from '@common/components/NotifyModal';
import { DuplicateProductError, DuplicateProductErrorMode } from '@common/components/DuplicateProductError';
import { ValidateProductErrors } from '../ValidateProductErrors/ValidateProductErrors';
import { useFeatureFlags } from '@common/hooks/useFeatureFlags';
import { selectConfiguration } from '@common/features/configuration/selectors';
import { useAppSelector } from '@common/hooks/redux';

export const tabsLabelsWrapperStyle: React.CSSProperties = {
  paddingLeft: '40px',
};

const errorIcon = '/icons/small_warning_icon.svg';

interface ISubmissionProductContent {
  product?: IProductDto;
  submission: ISubmissionDto;
  productInfoReadonlySections?: ProductInfoReadonlySections;
  associatedContentReadonlySections?: AssociatedContentReadonlySections;
  isProductRetired?: boolean;
  onCancel: () => void;
  onResultSuccess?: (() => void) | null;
  onDuplicateProductView?: (duplicateInfo: IDuplicateProductInfo) => void;
  shouldDisplayAmpIds: boolean;
  shouldDisplayContactGroup: boolean;
  shouldDisableDiscontinuationEdit?: boolean;
  taskStatus?: SubmissionTaskStatuses;
  isAdmin?: boolean;
}

const SubmissionProductContent = ({
  product,
  submission,
  productInfoReadonlySections = {},
  associatedContentReadonlySections,
  isProductRetired,
  onCancel,
  onResultSuccess,
  onDuplicateProductView,
  shouldDisplayAmpIds,
  shouldDisplayContactGroup,
  shouldDisableDiscontinuationEdit = false,
  taskStatus,
  isAdmin = false,
}: ISubmissionProductContent) => {
  const { state } = useLocation();
  const [selectedTab, setSelectedTab] = useState<string>(state?.tab ?? ProductTabs.ProductInfo);
  const [isValidateOpen, setIsValidateOpen] = useState(false);

  const [conflictMessage, setConflictMessage] = useState<JSX.Element | null>(null);

  const productId = product?.productId!;
  const submissionId = submission.submissionId;

  const activeIngredients = useMemo(() => product?.activeIngredients ?? [], [product?.activeIngredients]);

  const productType = getProductType(submission?.productGroup.productType);

  const productGroupName = submission?.productGroup.productGroupName ?? '';

  const documents = submission?.documents ?? [];
  const { disableLegalCategoriesEditForPPC } = useFeatureFlags();

  const hasSmpcOrPilUpdate = documents.some(
    (doc) => (doc.type === DocumentType.Smpc || doc.type === DocumentType.Pil) &&
             (doc.state === DocumentState.New || doc.state === DocumentState.Updated)
  );

  // Legal categories editing rules:
  // - Admin: can only edit for NEW products
  // - Customer: can edit for NEW products OR non-new products with SmPC/PIL update
  const readonlyLegalCategories = 
    disableLegalCategoriesEditForPPC && 
    product?.changeType !== ProductChangeType.New &&
    (isAdmin || !hasSmpcOrPilUpdate);

  const {
    productFormMethods,
    handleSubmit: handleSubmitProductForm,
    formState: { dirtyFields, errors },
    isProductDetailsValid,
    isProductInfoValid,
    isAssociatedContentValid,
    documentIdAfterSelectionChanged,
    draftOrPublishedDocuments,
    showAssociatedContentWarning,
    handleFileOpen,
    handleSelect,
    handleConfirm,
  } = useProductForm({ productGroupName, product, documents, activeIngredients, shouldDisplayAmpIds });

  const {
    duplicateProductInfo,
    handleDuplicateError,
    handleDuplicateErrorClose,
    handleDuplicateProductView,
  } = useDuplicateProductError({
    onDuplicateProductView,
    getConflictingName: (sourceField) =>
      sourceField === 'medicinalProductName'
        ? productFormMethods.getValues('productInfo.regulatorApprovedName')
        : productFormMethods.getValues('productInfo.name'),
  });

  const documentAfterSelectionChanged = getDocumentAfterSelectionChanged(
    draftOrPublishedDocuments,
    documentIdAfterSelectionChanged,
  );

  const associatedProducts = getAssociatedProducts(
    productId,
    submission?.products!,
    documentAfterSelectionChanged?.linkedProductIds!,
  );

  const { updateProductDetails, isUpdatingProductDetails } = useUpdateProductDetails(submissionId, productId);

  const resultActions = {
    onSuccess: () => {
      toast.success('Draft saved');
      if (onResultSuccess) onResultSuccess();
    },
    onError: (error: any) => {
      if (error?.cause === 409) {
        if (error?.data?.errorMessage?.productId) {
          handleDuplicateError(error.data.errorMessage);
          return;
        }

        if (error?.data?.message) {
          const fullErrorMessage = (
            <>
              {error.data.message}
              <br />
              <br />
              {SubstanceErrors.notInProductGroup} <br />
              Contact: <a href="mailto:quality@medicines.org.uk">quality@medicines.org.uk</a>
            </>
          );

          setConflictMessage(fullErrorMessage);
          return;
        }
      }
    },
  };

  const handleUpdateProductDetails = () => {
    if (!isUpdatingProductDetails) {
      handleSubmitProductForm(
        (data: IProductForm) => {
          updateProductDetails(prepareUpdateProductDetailsData(data, dirtyFields, product!), resultActions);
        },
        () => setIsValidateOpen(true),
      )();
    }
  };

  const handleCancelValidationModal = () => setIsValidateOpen(false);

  const getItems = useCallback(() => {
    const items = [
      {
        label: ProductTabs.ProductInfo,
        content: (
          <ProductInfo
            product={product!}
            productType={productType}
            activeIngredients={activeIngredients}
            productGroupName={productGroupName}
            showError={!isProductInfoValid}
            productInfoReadonlySections={productInfoReadonlySections}
            shouldDisableDiscontinuationEdit={shouldDisableDiscontinuationEdit}
          />
        ),
        icon: !isProductInfoValid ? errorIcon : undefined,
      },
      {
        label: ProductTabs.AssociatedContent,
        content: (
          <AssociatedContent
            submissionId={submissionId}
            productId={productId}
            showMissingAssociationWarning={showAssociatedContentWarning}
            showRetiredProductWarning={!!isProductRetired}
            documents={draftOrPublishedDocuments}
            onSelect={handleSelect}
            onFileOpen={
              product?.changeType !== ProductChangeType.Unchanged
                ? (documentId: Id) => handleFileOpen(submissionId, documentId)
                : undefined
            }
            disabledSections={associatedContentReadonlySections}
          />
        ),
        icon: !isAssociatedContentValid ? errorIcon : undefined,
      },
      {
        label: ProductTabs.ProductDetails,
        content: (
          <ProductDetails
            shouldDisplayContactGroup={shouldDisplayContactGroup}
            shouldDisplayAmpIds={shouldDisplayAmpIds}
            readonlyLegalCategories={readonlyLegalCategories}
          />
        ),
        icon: !isProductDetailsValid ? errorIcon : undefined,
      },
      {
        label: ProductTabs.ProductHistory,
        content: <ProductHistory productId={productId.toString()} />,
        icon: undefined,
      },
    ];

    return items;
  }, [
    activeIngredients,
    associatedContentReadonlySections,
    draftOrPublishedDocuments,
    handleFileOpen,
    handleSelect,
    isAssociatedContentValid,
    isProductDetailsValid,
    isProductInfoValid,
    isProductRetired,
    product,
    productGroupName,
    productInfoReadonlySections,
    productType,
    shouldDisplayAmpIds,
    shouldDisplayContactGroup,
    shouldDisableDiscontinuationEdit,
    showAssociatedContentWarning,
    submissionId,
    productId,
    readonlyLegalCategories,
  ]);

  const mappedErrors = mapErrors(errors);
  const configuration = useAppSelector(selectConfiguration);
  const productUrl = configuration.emcLink + '/product/' + productId;

  return (
    <>
      <Styled.LayoutWrapper>
        <SubmissionFlowHeader title={product?.name} emcUrl={productUrl} emcUrlTitle="emc product url">
          <HeaderBreadcrumb>
            <Link>Product Family</Link> &gt; <Link>Product Group</Link> &gt; Product
          </HeaderBreadcrumb>
        </SubmissionFlowHeader>
        <Styled.ProductWrap>
          <FormProvider {...productFormMethods}>
            <Tabs
              initialTab={state?.tab}
              items={getItems()}
              styles={{
                tabsLabelsWrapper: tabsLabelsWrapperStyle,
              }}
              selectedTab={selectedTab}
              onTabSelected={(selectedTab) => setSelectedTab(selectedTab)}
            />
          </FormProvider>
        </Styled.ProductWrap>
      </Styled.LayoutWrapper>
      <SubmissionFlowFooter onCancel={onCancel} onContinue={handleUpdateProductDetails} continueText="Save changes" />
      <AlreadyAssociatedProductsModal
        isOpen={!!documentIdAfterSelectionChanged && (documentAfterSelectionChanged?.linkedProductIds?.length ?? 0) > 1}
        document={documentAfterSelectionChanged?.documentTitle ?? ''}
        products={associatedProducts}
        onConfirm={handleConfirm}
      />
      <NotifyModal
        isOpen={isValidateOpen}
        title="Please add the following missing information"
        onClose={handleCancelValidationModal}
      >
        <ValidateProductErrors
          productInfoErrors={mappedErrors.productInfoErrors}
          associatedContentErrors={mappedErrors.associatedContentErrors}
          productDetailsErrors={mappedErrors.productDetailsErrors}
        />
      </NotifyModal>

      <NotifyModal
        isOpen={conflictMessage != null}
        title="Cannot save changes"
        onClose={() => {
          setConflictMessage(null);
        }}
        cancelText="Close"
      >
        <div style={{ textAlign: 'justify' }}>{conflictMessage}</div>
      </NotifyModal>

      <DuplicateProductError
        mode={DuplicateProductErrorMode.Update}
        isOpen={duplicateProductInfo != null}
        productName={duplicateProductInfo?.conflictingName ?? product?.regulatorApprovedName ?? ''}
        onClose={handleDuplicateErrorClose}
        onProductView={handleDuplicateProductView}
        conflictField={duplicateProductInfo?.conflictField}
      />
    </>
  );
};

export default SubmissionProductContent;
